'use client';

import Link from 'next/link';
import { useState, useEffect, useRef } from 'react';
import { useForumAuth, ForumUser } from '@/lib/useForumAuth';

const BellIcon = () => (
  <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
  </svg>
);

const DiscordIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M20.317 4.369a19.79 19.79 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.369a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.009c.12.098.246.198.373.293a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.891.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.331c-1.182 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
  </svg>
);

interface UIForumNotification {
  _id: string;
  type: string;
  fromUsername: string;
  fromAvatar: string;
  threadId?: string;
  threadTitle?: string;
  scriptName?: string;
  parentContent: string;
  replyContent: string;
  read: boolean;
  createdAt: string;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function NotificationBell({ currentUser }: { currentUser: ForumUser }) {
  const [notifications, setNotifications] = useState<UIForumNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const fetchNotifications = async () => {
    try {
      const res = await fetch('/api/notifications');
      if (!res.ok) return;
      const data = await res.json();
      if (data && Array.isArray(data.notifications)) {
        setNotifications(data.notifications);
        setUnreadCount(data.unreadCount || 0);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser.discordId]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const toggleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next && unreadCount > 0) {
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      try {
        await fetch('/api/notifications/read', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });
      } catch (e) {
        console.error(e);
      }
    }
  };

  return (
    <div className="notif-wrap" ref={wrapRef}>
      <button className="notif-bell" onClick={toggleOpen} title="Notifications" aria-label="Notifications">
        <BellIcon />
        {unreadCount > 0 && <span className="notif-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>}
      </button>
      {open && (
        <div className="notif-dropdown">
          <div className="notif-dropdown-header">Notifications</div>
          {notifications.length === 0 ? (
            <div className="notif-empty">No notifications yet.</div>
          ) : (
            <div className="notif-list">
              {notifications.map((n) => {
                const where = n.threadTitle || n.scriptName || '';
                const verb = n.type === 'thread_reply' ? 'replied to your thread' : 'replied to your message';
                const inner = (
                  <>
                    <div className="user-avatar notif-avatar">
                      {n.fromAvatar ? (
                        <img src={n.fromAvatar} alt={n.fromUsername} />
                      ) : (
                        (n.fromUsername || 'U')[0].toUpperCase()
                      )}
                    </div>
                    <div className="notif-body">
                      <p className="notif-text">
                        <strong>{n.fromUsername}</strong> {verb}
                        {where ? (
                          <>
                            {' '}in <span className="notif-script">{where}</span>
                          </>
                        ) : null}
                      </p>
                      {n.parentContent && <p className="notif-quote">“{n.parentContent}”</p>}
                      <p className="notif-reply">{n.replyContent}</p>
                      <span className="notif-time">{timeAgo(n.createdAt)}</span>
                    </div>
                    {!n.read && <span className="notif-dot" />}
                  </>
                );
                return n.threadId ? (
                  <Link key={n._id} href={`/forum/${n.threadId}`} className={`notif-item ${n.read ? '' : 'unread'}`} onClick={() => setOpen(false)}>
                    {inner}
                  </Link>
                ) : (
                  <div key={n._id} className={`notif-item ${n.read ? '' : 'unread'}`}>
                    {inner}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ForumHeader() {
  const { currentUser, login, logout } = useForumAuth();

  return (
    <header className="topbar">
      <Link href="/forum" className="logo">
        <span>devforum</span>
        <span className="logo-suffix">
          <span className="red">.</span>
          <span>blade</span>
          <span className="green">ball</span>
        </span>
      </Link>

      <nav className="nav-center">
        <Link href="/forum" className="nav-link">
          <span>Forum</span>
        </Link>
        <Link href="/" className="nav-link">
          <span>Scripts</span>
        </Link>
      </nav>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 60 }}>
        {currentUser ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <NotificationBell currentUser={currentUser} />
            <div className="user-avatar" style={{ width: 28, height: 28, fontSize: 11 }}>
              {currentUser.avatar ? (
                <img src={currentUser.avatar} alt={currentUser.username} />
              ) : (
                currentUser.username[0].toUpperCase()
              )}
            </div>
            <span style={{ fontFamily: 'var(--font-poppins)', fontSize: 13, color: '#fff', fontWeight: 600 }}>
              {currentUser.username}
            </span>
            <button onClick={logout} className="comment-action-link" style={{ padding: '4px 8px', fontSize: 11 }}>
              Logout
            </button>
          </div>
        ) : (
          <button onClick={login} className="themes-btn" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <DiscordIcon />
            Login with Discord
          </button>
        )}
      </div>
    </header>
  );
}
